import { useEcosystemSnapshot } from "../hooks/use-intelligence-queries.ts";
import { exportCsv } from "../lib/csv-export.ts";

function formatDollars(n: number | null): string {
  if (n == null || n === 0) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

export function EcosystemSnapshot() {
  const { data, isLoading, isError } = useEcosystemSnapshot(5);

  if (isLoading && !data) return <div className="intel-loading">Loading ecosystem snapshot…</div>;
  if (isError && !data) return <div className="intel-error">Failed to load ecosystem snapshot</div>;
  if (!data) return null;

  const handleExport = () => {
    exportCsv(
      "intelligence-ecosystem-snapshot.csv",
      ["Ecosystem", "Projects", "Total committed (USD)", "Median raise (USD)", "Median score"],
      data.map((r) => [
        r.ecosystem,
        r.projectCount,
        r.totalCommittedUsd,
        r.medianRaiseUsd ?? "",
        r.medianScore != null ? r.medianScore.toFixed(1) : "",
      ])
    );
  };

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <div className="intel-section__header-row">
          <div>
            <h3 className="intel-section__title">Ecosystem snapshot</h3>
            <p className="intel-section__subtitle">
              Per-chain distribution of our pre-TGE coverage. A project counts toward every
              ecosystem it touches, so multichain projects appear in multiple rows (totals exceed
              the global project count on purpose). Ecosystems with fewer than 5 tracked projects
              are filtered to stay out of low-sample territory.
            </p>
          </div>
          {data.length > 0 && (
            <button
              type="button"
              className="intel-csv-btn"
              onClick={handleExport}
              aria-label="Download ecosystem snapshot as CSV"
            >
              Download CSV
            </button>
          )}
        </div>
      </header>

      {data.length === 0 ? (
        <div className="intel-coinv-empty">No ecosystems with ≥5 tracked projects.</div>
      ) : (
        <div className="intel-eco-wrap">
          <table className="intel-eco">
            <thead>
              <tr>
                <th>Ecosystem</th>
                <th className="intel-eco__num-head">Projects</th>
                <th className="intel-eco__num-head">Committed</th>
                <th className="intel-eco__num-head">Median raise</th>
                <th className="intel-eco__num-head">Median score</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.ecosystem}>
                  <td className="intel-eco__chain">{row.ecosystem}</td>
                  <td className="intel-eco__num">{row.projectCount}</td>
                  <td className="intel-eco__num">{formatDollars(row.totalCommittedUsd)}</td>
                  <td className="intel-eco__num">{formatDollars(row.medianRaiseUsd)}</td>
                  <td className="intel-eco__num">
                    {row.medianScore != null ? row.medianScore.toFixed(1) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
